const API_URL = import.meta.env.VITE_API_URL || "http://localhost:5000/api/v1";
import axiosInstance from './axiosConfig';

export interface Product {
  id: string;
  name: string;
  description: string;
  price: number;
  salesPrice: number;
  stock: number;
  sold: number;
  rating: number;
  sku: string;
  quantity: number;
  discount: number;
  discountType: 'percentage' | 'fixed';
  isFeatured: boolean;
  onSale: boolean;
  featuredImage: string;
  images: string[];
  video?: string;
  videoUrl?: string;
  videos?: string[];
  featuresAndSpecifications: {
    description: string;
    images: string[];
  };
  additionalInformation: {
    description: string;
    images: string[];
  };
  category: {
    id: string;
    name: string;
    description: string;
    slug: string;
    image: string;
  };
  tags: string[];
}

export interface PaginationMetadata {
  totalItems: number;
  totalPages: number;
  currentPage: number;
  limit: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
  nextPage: number | null;
  prevPage: number | null;
}

export interface ProductResponse {
  success: boolean;
  message: string;
  data: Product[] | {
    products: Product[];
    onSaleProducts: Product[];
  } | any;
  pagination?: PaginationMetadata;
}

export interface Tag {
  id: string;
  name: string;
  slug?: string;
}

const getAuthHeader = () => ({
  'Authorization': `Bearer ${localStorage.getItem('token')}`,
  'Content-Type': 'application/json'
});

class ProductService {
  // Admin APIs
  async getAdminProducts(page: number = 1, limit: number = 10, id?: string): Promise<ProductResponse> {
    try {
      const params: any = {
        page,
        limit,
        sort: 'latest'
      };
      if (id) {
        params.id = id;
        delete params.page;
        delete params.limit;
        delete params.sort;
      }
      const response = await axiosInstance.get(`${API_URL}/admin/product`, {
        headers: getAuthHeader(),
        params
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async getAdminProductById(id: string): Promise<Product> {
    try {
      const response = await axiosInstance.get(`${API_URL}/admin/product`, {
        headers: getAuthHeader(),
        params: {
          id
        }
      });
      return response.data?.data || response.data;
    } catch (error) {
      throw error;
    }
  }

  async createProduct(productData: FormData): Promise<Product> {
    try {
      const response = await axiosInstance.post(`${API_URL}/admin/product`, productData, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'multipart/form-data'
        },
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async updateProduct(id: string, productData: FormData): Promise<Product> {
    try {
      const response = await axiosInstance.put(`${API_URL}/admin/product/${id}`, productData, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'multipart/form-data'
        },
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async deleteProduct(id: string): Promise<void> {
    try {
      await axiosInstance.delete(`${API_URL}/admin/product/${id}`, {
        headers: getAuthHeader(),
      });
    } catch (error) {
      throw error;
    }
  }

  // TODO: Replace the product-derived tag list in ProductsPage with this API
  // once the backend tags endpoint is ready.
  async getAdminTags(): Promise<Tag[]> {
    try {
      const response = await axiosInstance.get(`${API_URL}/admin/tags`, {
        headers: getAuthHeader(),
      });
      return response.data?.data || response.data || [];
    } catch (error) {
      throw error;
    }
  }

  // Public APIs
  async getProducts(page: number = 1, limit: number = 10, id?: string): Promise<ProductResponse> {
    try {
      const params: any = {
        page,
        limit,
        sort: 'latest'
      };
      if (id) {
        params.id = id;
      }
      const response = await axiosInstance.get(`${API_URL}/product`, {
        params
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }
}

export const productService = new ProductService(); 
